import { Navigate } from 'react-router-dom';

const LogisticManagementAddressBookEdit = async () =>
    import('../../pages/settings/address-book/Edit').then((module) => ({
        Component: module.LogisticManagementAddressBookEdit,
    }));
const LogisticManagementAddressBookNew = async () =>
    import('../../pages/settings/address-book/New').then((module) => ({
        Component: module.LogisticManagementAddressBookNew,
    }));
const SettingsViewPage = async () =>
    import('../../pages/settings/View').then((module) => ({ Component: module.SettingViewPage }));
const SettingsShipperViewPage = async () =>
    import('../../pages/settings/shippers/ShipperView').then((module) => ({ Component: module.ShipperView }));
const SettingsTemplateViewPage = async () =>
    import('../../pages/settings/settings-template/SettingsTemplateView').then((module) => ({
        Component: module.SettingsTemplateView,
    }));
const SettingsCarrierUsersListPage = async () =>
    import('../../pages/settings/users/List').then((module) => ({
        Component: module.UsersList,
    }));
const GorgiasDetailPage = async () =>
    import('../../pages/settings/connectors/gorgias/GorgiasDetail').then((module) => ({
        Component: module.GorgiasDetail,
    }));
const ShopifyDetailPage = async () =>
    import('../../pages/settings/connectors/shopify/ShopifyDetail').then((module) => ({
        Component: module.ShopifyDetail,
    }));
const ShopifyOnboardingPage = async () =>
    import('../../pages/settings/connectors/shopify/ShopifyOnboardingPage').then((module) => ({
        Component: module.ShopifyOnboardingPage,
    }));

export const getSettingsRoutes = (user) => {
    const hasWarehouses = user?.customerWarehouseConfigurations?.length > 0;
    const isLsp = !!user?.logisticProviderId;
    const isShipper = !!user?.customerId;
    const transportIsConfiguredOnLsp = user?.logisticProvider?.settings?.transportOrdersManagementEnabled;
    const transportIsConfiguredOnShipper = user?.customer?.settings?.transportOrdersManagementEnabled;
    const isAppAdmin = !!user?.isAppAdmin;

    return [
        // home
        {
            path: '/settings/',
            element: <Navigate to='/settings/users/' />,
            redirectFrom: ['/settings'],
        },
        // users
        {
            path: '/settings/users/',
            lazy: SettingsViewPage,
            redirectFrom: ['/settings/users'],
        },
        {
            path: '/users/',
            element: <Navigate to='/settings/users/' />,
            redirectFrom: ['/users'],
        },

        // users
        {
            path: '/settings/shipper-users/',
            lazy: SettingsViewPage,
            condition: isLsp && (hasWarehouses || transportIsConfiguredOnShipper),
            redirectFrom: ['/settings/shipper-users'],
        },
        // shippers
        {
            path: '/settings/shippers/',
            lazy: SettingsViewPage,
            condition: isLsp && (hasWarehouses || transportIsConfiguredOnShipper),
            redirectFrom: ['/settings/shippers'],
        },
        {
            path: '/settings/shippers/:customerId/users',
            lazy: SettingsShipperViewPage,
            condition: isLsp && (hasWarehouses || transportIsConfiguredOnLsp),
        },

        {
            path: '/settings/shippers/:customerId/custom-fields',
            lazy: SettingsShipperViewPage,
            condition: isLsp && (hasWarehouses || transportIsConfiguredOnLsp),
        },
        {
            path: '/settings/shippers/:customerId/carriers',
            lazy: SettingsShipperViewPage,
            condition: isLsp && (hasWarehouses || transportIsConfiguredOnLsp),
        },
        {
            path: '/settings/shippers/:customerId/incidents',
            lazy: SettingsShipperViewPage,
            condition: isLsp && (hasWarehouses || transportIsConfiguredOnLsp),
        },
        {
            path: '/settings/shippers/:customerId/document-types',
            lazy: SettingsShipperViewPage,
            condition: isLsp,
        },
        {
            path: '/settings/shippers/:customerId/additional-request-types',
            lazy: SettingsShipperViewPage,
            condition: isLsp,
        },
        {
            path: '/settings/shippers/:customerId/display',
            lazy: SettingsShipperViewPage,
            condition: isLsp && (hasWarehouses || transportIsConfiguredOnLsp),
        },
        // settings template
        {
            path: '/settings-template/custom-fields',
            lazy: SettingsTemplateViewPage,
            condition: isLsp,
        },
        {
            path: '/settings-template/carriers',
            lazy: SettingsTemplateViewPage,
            condition: isLsp,
        },
        {
            path: '/settings-template/incidents',
            lazy: SettingsTemplateViewPage,
            condition: isLsp,
        },
        {
            path: '/settings-template/document-types',
            lazy: SettingsTemplateViewPage,
            condition: isLsp,
        },
        {
            path: '/settings-template/additional-request-types',
            lazy: SettingsTemplateViewPage,
            condition: isLsp,
        },
        // storages
        {
            path: '/settings/storages/',
            lazy: SettingsViewPage,
            redirectFrom: ['/settings/storages'],
            condition: hasWarehouses,
        },
        // warehouses
        {
            path: '/settings/warehouses/',
            lazy: SettingsViewPage,
            redirectFrom: ['/settings/warehouses'],
            condition: hasWarehouses,
        },
        // address book
        {
            path: '/settings/address-book/',
            lazy: SettingsViewPage,
            condition:
                ((hasWarehouses && user?.features?.orderManagementEnabled) || transportIsConfiguredOnShipper) &&
                user?.role === 'SHIPPER',
        },
        {
            path: '/settings/address-book/:addressId/edit',
            lazy: LogisticManagementAddressBookEdit,
            condition:
                ((hasWarehouses && user?.features?.orderManagementEnabled) || transportIsConfiguredOnShipper) &&
                user?.role === 'SHIPPER',
        },
        {
            path: '/settings/address-book/new',
            lazy: LogisticManagementAddressBookNew,
            condition:
                ((hasWarehouses && user?.features?.orderManagementEnabled) || transportIsConfiguredOnShipper) &&
                user?.role === 'SHIPPER',
        },
        // custom field
        {
            path: '/settings/custom-fields/',
            lazy: SettingsViewPage,
            condition:
                ((hasWarehouses && user?.features?.orderManagementEnabled) || transportIsConfiguredOnShipper) &&
                user?.role === 'SHIPPER',
        },
        // Carriers page
        {
            path: '/settings/carriers/',
            lazy: SettingsViewPage,
            condition: (hasWarehouses || transportIsConfiguredOnShipper) && user?.role === 'SHIPPER',
        },
        {
            path: '/settings/carriers/:customerCarrierId/users/',
            lazy: SettingsCarrierUsersListPage,
            condition: (hasWarehouses || transportIsConfiguredOnShipper) && user?.role === 'SHIPPER',
        },
        // Incidents types page
        {
            path: '/settings/incidents/',
            lazy: SettingsViewPage,
            condition: hasWarehouses && user?.role === 'SHIPPER',
        },
        // Display
        {
            path: '/settings/display/',
            lazy: SettingsViewPage,
            condition: hasWarehouses || transportIsConfiguredOnShipper,
        },
        // Documents
        {
            path: '/settings/document-types/',
            lazy: SettingsViewPage,
            condition: user?.role === 'SHIPPER',
        },
        // Additional request
        {
            path: '/settings/additional-request-types/',
            lazy: SettingsViewPage,
            condition: user?.role === 'SHIPPER',
        },
        // Dashboards
        {
            path: '/settings/dashboards/',
            lazy: SettingsViewPage,
            condition: isAppAdmin,
        },
        // Appointments
        {
            path: '/settings/appointments/',
            lazy: SettingsViewPage,
            redirectFrom: ['/settings/appointments'],
            condition: user?.role === 'PROVIDER',
        },
        // Connectors
        {
            path: '/settings/connectors/',
            lazy: SettingsViewPage,
        },
        {
            path: '/settings/connectors/gorgias/',
            lazy: GorgiasDetailPage,
        },
        {
            path: '/settings/connectors/gorgias/:action',
            lazy: GorgiasDetailPage,
        },
        {
            path: '/settings/connectors/shopify/',
            lazy: ShopifyDetailPage,
            condition: isShipper,
            children: [
                {
                    path: 'onboarding/',
                    lazy: ShopifyOnboardingPage,
                    condition: isShipper,
                },
            ],
        },
        {
            path: '/settings/connectors/shopify/:action',
            lazy: ShopifyDetailPage,
            condition: isShipper,
        },
        {
            path: '/settings/*',
            element: <Navigate to='/settings' />,
        },
    ];
};
